<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :card-body-class="['d-flex', 'flex-column', 'justify-content-center']"
        :min-w="3"
        :min-h="minHeight"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <b-row
                class="flex-row"
                v-if="currentWeatherCondition.id"
            >
                <b-col class="mb-md-0 mb-4">
                    <div class="weather-today ml-md-0 ml-2">
                        <span class="weather-today_icon-wrapper">
                            <i
                                class="weather-today__icon"
                                :class="`wi wi-owm-${currentWeatherCondition.id}`"
                                :style="{
                                    color: getCurrentIconColor(
                                        currentWeatherCondition.id
                                    ),
                                }"
                            ></i>
                        </span>
                        <span class="weather-today_city">
                            {{ currentLocationName }}
                        </span>
                        <span class="weather-today_details">
                            {{ currentWeatherCondition.description }}
                        </span>
                    </div>
                </b-col>
                <b-col>
                    <div class="weather-details">
                        <span class="weather-details_degree"
                            >{{
                                currentWeather.temperature === 0 ||
                                currentWeather.temperature
                                    ? currentWeather.temperature.toFixed(0)
                                    : ''
                            }}<sup> <span v-html="currentUnit" /></sup
                        ></span>
                        <div class="d-flex flex-column">
                            <span class="weather-details_humidity">
                                <i
                                    class="wi wi-raindrops weather-details_humidity-icon"
                                ></i>
                                {{ currentWeather.humidity }}%
                            </span>
                            <span class="weather-details_wind">
                                <i
                                    class="wi wi-strong-wind weather-details_wind-icon"
                                ></i>
                                {{
                                    currentWeather.wind_speed
                                        ? currentWeather.wind_speed.toFixed(
                                                0
                                            )
                                        : ''
                                }}
                                {{ currentWindSpeedUnit }}
                            </span>
                        </div>
                    </div>
                </b-col>
            </b-row>
            <div
                class="d-flex justify-content-around flex-wrap pt-4 pb-2"
                v-if="weeklyWeather.length"
            >
                <div
                    class="weather-week-item"
                    :key="weather.day"
                    v-for="weather in weeklyWeatherItems"
                >
                    <i
                        class="wi weather-week-item_icon"
                        :class="`wi-owm-${weather.conditions[0].open_weather_id}`"
                        :style="{
                            color: getCurrentIconColor(
                                weather.conditions[0].open_weather_id
                            ),
                        }"
                    />
                    <span class="weather-week-item_degree">
                        {{ weather.temperature.day.toFixed(0) }}
                        <span v-html="currentUnit" />
                    </span>
                    <span class="weather-week-item_day">
                        {{ formatDate(weather.date) }}
                    </span>
                </div>
            </div>
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
                :with-coords="true"
                :city="city"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import CardGridItem from '../common/card-grid-item.vue';
import {
    APP_CITY_LIST_ENDPOINT,
    DASHBOARD_WEATHER_WIDGET_CURRENT_WEATHER,
    DASHBOARD_WEATHER_WIDGET_DAILY_WEATHER,
} from '../../../../endpoints';
import FormInput from '../../../generic/form-input/form-input.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import { weatherUnits, STANDARD, METRIC, IMPERIAL } from '../helpers';
import { isLargeScreen, isMediumScreen } from '../../../../utils/screens';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    name: 'WeatherCardGridItem',
    mixins: [cardGridItemBaseMixin],
    components: {
        CardGridItem,
        TabWidgetSettingsModalForm,
        FormInput,
    },
    data() {
        return {
            defaultCity: null,
            defaultWeatherUnit: weatherUnits[IMPERIAL],
            currentWeather: {
                conditions: [],
                humidity: null,
                temperature: null,
                temperature_feels_like: null,
                wind_speed: null,
            },
            weeklyWeather: [],
        };
    },
    computed: {
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    weather_unit:
                        this.tabWidget.settings.weather_unit ||
                        this.defaultWeatherUnit,
                },
            };
        },
        city() {
            return this.tabWidget.settings.city || this.defaultCity;
        },
        currentLocationName() {
            return (
                this.tabWidget.settings?.city?.name || this.defaultCity?.name
            );
        },
        currentWeatherCondition() {
            if (this.currentWeather.conditions[0]) {
                return {
                    id: this.currentWeather.conditions[0].open_weather_id,
                    description: this.currentWeather.conditions[0].description,
                };
            } else {
                return {
                    id: '',
                    description: '',
                };
            }
        },
        currentUnit() {
            switch (this.tabWidget.settings.weather_unit) {
                case weatherUnits[IMPERIAL]:
                    return '&#8457;';
                case weatherUnits[METRIC]:
                    return '&#8451;';
                case weatherUnits[STANDARD]:
                    return '&#8490;';
                default:
                    return '&#8457;';
            }
        },
        currentWindSpeedUnit() {
            switch (this.tabWidget.settings.weather_unit) {
                case weatherUnits[IMPERIAL]:
                    return 'mph';
                case weatherUnits[METRIC]:
                    return 'm/sec';
                case weatherUnits[STANDARD]:
                    return 'm/sec';
                default:
                    return 'mph';
            }
        },
        weeklyWeatherItems() {
            return !isLargeScreen() ||
                !isMediumScreen() ||
                this.tabWidget.w <= 4
                ? this.weeklyWeather.slice(0, 5)
                : this.weeklyWeather;
        },
    },
    mounted() {
        this.fetchWeather();
    },
    methods: {
        getCurrentIconColor(id) {
            if ((id >= 600 && id <= 601) || (id >= 621 && id <= 622)) {
                return '#002dd8';
            } else if ((id >= 300 && id <= 531) || (id >= 602 && id <= 620)) {
                return 'gray';
            } else if (id === 800 || id === 904) {
                return '#caea00';
            } else if (id >= 771 && id <= 804 && id !== 800) {
                return '#3bbbdf';
            } else {
                return '#44464f';
            }
        },
        async fetchWeather() {
            try {
                let lat = this.tabWidget.settings?.city?.latitude;
                let lon = this.tabWidget.settings?.city?.longitude;
                const unit =
                    this.tabWidget.settings?.weather_unit ||
                    this.defaultWeatherUnit;
                if (!lat && !lon) {
                    const defaultCityParams = {
                        limit: 5,
                        offset: 0,
                        search: 'orlando',
                        country: 1,
                    };
                    const response = await API.get(APP_CITY_LIST_ENDPOINT(), {
                        params: defaultCityParams,
                    });
                    lat = response.data[0].latitude;
                    lon = response.data[0].longitude;
                    this.defaultCity = response.data[0];
                }
                Promise.all([
                    this.fetchCurrentWeather(lat, lon, unit),
                    this.fetchWeeklyWeather(lat, lon, unit),
                ]);
            } catch (e) {
                console.log(e);
                //this.errorToast({ text: 'Could not fetch current weather' });
            }
        },
        async fetchCurrentWeather(latitude, longitude, unit) {
            try {
                const currentWeatherResponse = await API.get(
                    DASHBOARD_WEATHER_WIDGET_CURRENT_WEATHER(
                        latitude,
                        longitude,
                        unit
                    )
                );
                this.currentWeather = currentWeatherResponse.data;
            } catch (e) {
                console.log(e);
                //this.errorToast({ text: 'Could not fetch current weather' });
            }
        },
        async fetchWeeklyWeather(latitude, longitude, unit) {
            try {
                const weeklyWeatherResponse = await API.get(
                    DASHBOARD_WEATHER_WIDGET_DAILY_WEATHER(
                        latitude,
                        longitude,
                        unit
                    )
                );
                this.weeklyWeather = weeklyWeatherResponse.data;
            } catch (e) {
                console.log(e);
                //this.errorToast({ text: 'Could not fetch current location' });
            }
        },
        formatDate(date) {
            return format(parseISO(date), 'EEE');
        },
    },
}
</script>

<style lang="scss" scoped>
@import 'weather-icons/css/weather-icons.min.css';
@import 'weather-icons/css/weather-icons-wind.min.css';

.weather-week-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    &_icon {
        font-size: 2rem;
    }
    &_degree {
        color: #ffffff;
        background-color: #3e3d3d;
        padding: 0 5px;
        border-radius: 5px;
        margin: 10px 0;
        font-size: 1rem;
    }
    &_day {
        font-weight: bold;
        font-size: 1rem;
        color: #44464f;
    }
}
.weather-today {
    display: flex;
    flex-direction: column;
    align-items: center;
    .weather-today_icon-wrapper {
        margin-bottom: 10px;
        .weather-today__icon {
            font-size: 6rem;

            @media (max-width: 400px) {
                font-size: 4rem;
            }
        }
    }
    &_city {
        margin: 15px 0 15px 0;
        font-size: 2rem;
        font-weight: bold;
        line-height: 1em;
        text-align: center;

        @media (max-width: 400px) {
            font-size: 1.5rem;
        }
    }
    &_details {
        color: #44464f;
        font-size: 1.5rem;
        text-transform: capitalize;
        text-align: center;

        @media (max-width: 400px) {
            font-size: 1.2rem;
        }
    }
}
.weather-details {
    display: flex;
    align-items: center;
    flex-direction: column;
    color: #44464f;
    margin-top: 20px;
    &_temperature {
        margin-bottom: 20px;
        font-size: 1.5rem;
        &-icon {
            width: 20px;
            height: 20px;
        }
    }
    &_degree {
        display: flex;
        align-items: center;
        margin-bottom: 30px;
        font-size: 4rem;
        font-weight: bold;
        color: var(--dark-gray);
        sup {
            font-size: 1.5rem;
        }

        @media (max-width: 400px) {
            font-size: 3rem;
            &-icon {
                font-size: 1.2rem;
            }
        }
    }
    &_humidity {
        margin-bottom: 20px;
        font-size: 1.5rem;
        &-icon {
            font-size: 2rem;
        }

        @media (max-width: 400px) {
            font-size: 1.2rem;
            &-icon {
                font-size: 1.5rem;
            }
        }
    }
    &_wind {
        font-size: 1.5rem;
        &-icon {
            font-size: 2rem;
        }

        @media (max-width: 400px) {
            font-size: 1.2rem;
            &-icon {
                font-size: 1.5rem;
            }
        }
    }
}
</style>
